// src/components/squads/squad-constants.ts
//
// Constantes e labels PT-BR da tela de Squads.
//
// Os valores da esquerda são os do banco (`squads.status`, `squad_runs.status`,
// `handoffs.status`) — não inventar sinônimos aqui. Quando um valor novo
// aparecer, o fallback mostra o valor cru em vez de esconder: rótulo faltando é
// um bug de tradução, não motivo para a tela mentir que o estado é outro.

import type { SquadScope } from '@/lib/api';

export const SCOPE_BADGE: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
  global: 'info',
  tenant: 'success',
};

/** `squads.status` real do backend. */
export const STATUS_BADGE: Record<string, 'default' | 'info' | 'success' | 'warning' | 'danger'> = {
  draft: 'default',
  active: 'success',
  deprecated: 'warning',
  archived: 'danger',
};

export const STATUS_LABELS: Record<string, string> = {
  draft: 'rascunho',
  active: 'ativa',
  deprecated: 'depreciada',
  archived: 'arquivada',
};

/** `squad_runs.status` — quatro terminais + os de trânsito. */
export const RUN_STATUS_BADGE: Record<string, 'default' | 'info' | 'success' | 'warning' | 'danger'> = {
  pending: 'default',
  running: 'info',
  completed: 'success',
  failed: 'danger',
  cancelled: 'default',
  escalated: 'warning',
};

export const RUN_STATUS_LABELS: Record<string, string> = {
  pending: 'aguardando',
  running: 'executando',
  completed: 'concluída',
  failed: 'falhou',
  cancelled: 'cancelada',
  escalated: 'escalada',
};

/** `handoffs.status`. `submitted` NÃO é terminal — está entregue, não resolvido. */
export const HANDOFF_STATUS_LABELS: Record<string, string> = {
  proposed: 'proposto',
  accepted: 'aceito',
  submitted: 'entregue',
  approved: 'aprovado',
  rejected: 'rejeitado',
  cancelled: 'cancelado',
  timeout: 'expirado',
};

export const HANDOFF_STATUS_BADGE: Record<string, 'default' | 'info' | 'success' | 'warning' | 'danger'> = {
  proposed: 'default',
  accepted: 'info',
  submitted: 'info',
  approved: 'success',
  rejected: 'danger',
  cancelled: 'default',
  timeout: 'warning',
};

/** `squad_run_members.status`. */
export const MEMBER_STATUS_LABELS: Record<string, string> = {
  activated: 'ativo',
  working: 'trabalhando',
  submitted: 'entregue',
  completed: 'concluído',
  failed: 'falhou',
  cancelled: 'cancelado',
};

/** Topologia declarada no SQUAD.md §2. */
export const TOPOLOGY_LABELS: Record<string, string> = {
  orchestrator_led: 'orquestrada',
  peer_to_peer: 'par a par',
  hierarchical: 'hierárquica',
  swarm: 'enxame',
};

export const SCOPE_FILTER_OPTIONS: Array<{ value: SquadScope | ''; label: string }> = [
  { value: '', label: 'Scope: todos' },
  { value: 'tenant', label: 'tenant' },
  { value: 'global', label: 'global' },
];

export const STATUS_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Status: todos' },
  { value: 'active', label: 'ativa' },
  { value: 'draft', label: 'rascunho' },
  { value: 'deprecated', label: 'depreciada' },
  { value: 'archived', label: 'arquivada' },
];

/** Rótulo com fallback para o valor cru — nunca esconder estado desconhecido. */
export function rotular(mapa: Record<string, string>, valor: string | null | undefined): string {
  if (!valor) return '—';
  return mapa[valor] ?? valor;
}

/**
 * Contagem que pode não ter sido possível.
 *
 * `null` vem do backend quando a query de contagem FALHOU. Renderizar "0" aqui
 * reintroduziria na tela a mentira que o backend tomou o cuidado de evitar:
 * "esta squad nunca executou" é uma afirmação, e não temos base para fazê-la.
 */
export function fmtContagem(n: number | null | undefined): string {
  return n == null ? '—' : String(n);
}

/** Duração legível entre dois instantes; "—" quando ainda não terminou. */
export function fmtDuracao(inicio: string | null, fim: string | null): string {
  if (!inicio || !fim) return '—';
  const ms = new Date(fim).getTime() - new Date(inicio).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}min ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}min`;
}
